/**
 * Inventory dashboard — `/dashboard/inventory`.
 * Convert 1:1 từ page "Inventory" của PBI_ERP.pbix (skill /convert-pbi).
 *  - Rows: Grade (category subtotal, đậm, expand/collapse) → code (thụt lề);
 *    grand Total pinned footer. Columns: warehouse (4 kho page-filter + Total)
 *    × 5 measures (Receive fr. Supplier / Issue to Pro. / Receive fr. Pro. /
 *    Shipped / Stocks).
 *  - DATA: Report DB qua `useGetInventoryDashboardQuery` — BE trả ROWS + warehouse
 *    list; FE chỉ render, KHÔNG tự tính số (memory `feedback_api_view_source_of_truth`).
 *
 * Table dùng template dataList GIỐNG `/dashboard/long-short` (skill /convert-table,
 * chị Đẹp 2026-06-10): FilterBar (Search + Columns) + per-column ▽ + resize +
 * pagination + freeze cột Grade + header 2 tầng (warehouse → measure). Giữ
 * hierarchy PBI: category → code, KHÔNG sort (đảo hàng phá subtotal).
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useMemo, useState, type CSSProperties } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../../components/BrandState'
import ResizeHandle from '../../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../../components/dataList'
import { useColumnWidths } from '../../../hooks/useColumnWidths'
import { useIsMobile } from '../../../hooks/useResponsive'
import { useLocalDatasheetColumns } from '../../../utils/datasheetColumns/useLocalDatasheetColumns'
import { FuturesColumnsMenu } from '../../futures/FuturesColumnsMenu'
import { useGetInventoryDashboardQuery } from '../../../store/api/dashboardInventoryApi'
import type { LongShortRow } from '../../../store/api/dashboardLongShortApi'
import { ReportPageToolbar } from '../../report/reportToolbarSlot'
import { useReportZoom } from '../_shared/useReportZoom'
import { fmtNum } from '../pnl/format'

interface Measure {
  key: string
  label: string
}

/** 5 measures theo PBI Inventory matrix (cùng thứ tự cột mỗi warehouse group). */
const MEASURES: Measure[] = [
  { key: 'receiveSp', label: 'Receive fr. Supplier' },
  { key: 'issue', label: 'Issue to Pro.' },
  { key: 'receivePro', label: 'Receive fr. Pro.' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'stock', label: 'Stocks' },
]

interface ColumnDef {
  key: string
  label: string
  group?: string
  sub: string
  kind?: 'number'
  align?: 'right'
  filter?: ColumnFilterKind
}

interface FlatRow extends Record<string, unknown> {
  id: string
  rowType: 'category' | 'code'
  category: string
  label: string
}

const LABEL_KEY = 'label'
const FROZEN_KEYS: ReadonlySet<string> = new Set([LABEL_KEY])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { [LABEL_KEY]: 150 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { [LABEL_KEY]: 120 }

const HEAD_FONT_SX = {
  fontSize: '0.66rem',
  lineHeight: 1.15,
  py: 0.5,
  px: { xs: 0.5, xl: 0.75 },
} as const
const BODY_FONT_SX = {
  fontSize: '0.68rem',
  lineHeight: 1.25,
  py: 0.35,
  px: { xs: 0.5, xl: 0.75 },
} as const

const colKey = (wh: string, measure: string) => `${wh}|${measure}`

export default function InventoryDashboardPage() {
  const { data, isLoading, isFetching, isError, refetch } = useGetInventoryDashboardQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const { zoom, control } = useReportZoom()
  const warehouses = useMemo(() => data?.warehouses ?? [], [data])
  const apiRows = useMemo(() => data?.rows ?? [], [data])

  // Cột build động theo warehouse API trả (Grade + warehouse × measure).
  const columns = useMemo<ColumnDef[]>(
    () => [
      { key: LABEL_KEY, label: 'Grade', sub: 'Grade', filter: 'text' },
      ...warehouses.flatMap((wh) =>
        MEASURES.map((m) => ({
          key: colKey(wh, m.key),
          label: `${wh} — ${m.label}`,
          group: wh,
          sub: m.label,
          kind: 'number' as const,
          align: 'right' as const,
        })),
      ),
    ],
    [warehouses],
  )

  // Flatten matrix rows cho template; grand Total tách riêng → pinned footer.
  const { flatRows, totalFlat } = useMemo(() => {
    const flatten = (r: LongShortRow): FlatRow => {
      const flat: FlatRow = {
        id: `${r.category}|${r.code ?? r.rowType}`,
        rowType: r.rowType === 'category' ? 'category' : 'code',
        category: r.category,
        label: r.rowType === 'category' ? r.category : (r.code ?? r.category),
      }
      for (const wh of warehouses) {
        for (const m of MEASURES) flat[colKey(wh, m.key)] = r.values[wh]?.[m.key] ?? null
      }
      return flat
    }
    const total = apiRows.find((r) => r.rowType === 'total')
    return {
      flatRows: apiRows.filter((r) => r.rowType !== 'total').map(flatten),
      totalFlat: total ? flatten(total) : null,
    }
  }, [apiRows, warehouses])

  // Global Search
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()

  // Per-column filters (▽ trong header — session-only)
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)

  // Category đang thu gọn (mặc định expand hết — như PBI screenshot).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggleCollapse = (cat: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })

  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: columns,
    storageKey: 'dashboard.inventory',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('dashboard.inventory')
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  // Filter áp lên CODE rows; row category giữ khi còn ≥1 code con pass —
  // bảo toàn hierarchy subtotal của PBI matrix. Collapse ẩn code con.
  const visibleFlat = useMemo(() => {
    const matches = (r: FlatRow) =>
      rowMatchesSearch(r as never, searchTerm, columns as never[])
      && rowMatchesAllColumnFilters(r as never, columnFilters.filters)
    const matchedIds = new Set<string>()
    const passingCats = new Set<string>()
    for (const r of flatRows) {
      if (r.rowType === 'code' && matches(r)) {
        matchedIds.add(r.id)
        passingCats.add(r.category)
      }
    }
    return flatRows.filter((r) =>
      r.rowType === 'category'
        ? passingCats.has(r.category)
        : matchedIds.has(r.id) && !collapsed.has(r.category),
    )
  }, [flatRows, searchTerm, columnFilters.filters, columns, collapsed])

  const codeCount = useMemo(
    () => visibleFlat.filter((r) => r.rowType === 'code').length,
    [visibleFlat],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return visibleFlat
    const start = page * rowsPerPage
    return visibleFlat.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, visibleFlat])

  // Tầng header group (warehouse): gộp run các cột visible LIỀN KỀ cùng `group`.
  // Cột Grade (group undefined) render rowSpan 2 ngay tại vị trí của nó.
  const headerRuns = useMemo(() => {
    const runs: { group: string | null; cols: ColumnDef[] }[] = []
    for (const col of visibleColumns) {
      const g = col.group ?? null
      const last = runs[runs.length - 1]
      if (last && g !== null && last.group === g) last.cols.push(col)
      else runs.push({ group: g, cols: [col] })
    }
    return runs
  }, [visibleColumns])

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, flatRows) : 'text'),
    [popoverColumn, flatRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(flatRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, flatRows],
  )

  const minTableWidth = Math.max(1100, visibleColumns.length * 84)
  const gridKey = `${rowsPerPage}:${page}:${visibleFlat.length}:${visibleColumns.length}`

  /** Leaf header cell — ▽ filter + resize; Grade frozen (inline style §7.6).
   *  KHÔNG sort: đảo hàng phá hierarchy category → code của matrix. */
  const renderLeafHeaderCell = (col: ColumnDef, rowSpan?: number) => {
    const key = col.key
    const isFrozen = FROZEN_KEYS.has(key)
    const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
    const headerStyle: CSSProperties = {}
    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
    if (isFrozen) {
      headerStyle.position = 'sticky'
      headerStyle.top = 0
      headerStyle.left = 0
      headerStyle.zIndex = 100
    }
    return (
      <TableCell
        key={key}
        align={col.align}
        rowSpan={rowSpan}
        className="app-table-header-cell"
        style={Object.keys(headerStyle).length ? headerStyle : undefined}
        sx={{
          ...HEAD_FONT_SX,
          // Override global "app-table-header-cell" cream/gold → solid forest.
          backgroundImage: 'none',
          backgroundColor: '#0b3633',
          color: '#fff',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          userSelect: 'text',
          ...(isFrozen ? {} : { position: 'relative' }),
        }}
      >
        {col.sub}
        <ColumnFilterIcon
          active={columnFilters.isActive(key)}
          onClick={(position) => setColPopover({ key, position })}
          ariaLabel={`Filter ${col.label}`}
        />
        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
      </TableCell>
    )
  }

  /** Body cell value — fmtNum 0 decimals (Kg), âm ngoặc + đỏ, 0/null blank. */
  const renderNumberCell = (row: FlatRow, col: ColumnDef) => {
    const key = col.key
    const isCategory = row.rowType === 'category'
    const w = getWidth(key)
    const { text, neg } = fmtNum(row[key] as number | null)
    return (
      <TableCell
        key={key}
        align="right"
        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
        sx={{
          ...BODY_FONT_SX,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'top',
          // Cashflows skin: only horizontal grid lines, no vertical/zebra.
          border: 0,
          borderBottom: '1px solid #e8ecef',
          bgcolor: isCategory ? '#e8f1ee' : '#fff',
          fontWeight: isCategory ? 700 : 400,
          color: neg ? '#d32f2f' : undefined,
        }}
      >
        {text}
      </TableCell>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        // Không tự thêm padding — Layout `<main>` đã có p; đồng bộ outer margin
        // với PnL/Long-Short để chuyển trang không lệch format.
        // Parent chain đã bounded → bảng fill flex thay vì clamp viewport (§7.9).
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      {/* Header — format đồng bộ PnL/Long-Short: title + report date.
          Portal NGUYÊN block lên block chung của ReportsPage khi nhúng. */}
      <ReportPageToolbar>
      <Paper
        variant="outlined"
        sx={{
          px: { xs: 1, sm: 1.5 }, py: 1,
          border: '1px solid #dfe3e8', borderRadius: '8px', bgcolor: '#fff',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5,
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.15 }}>
            INVENTORY
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#b28a3b', fontWeight: 600 }}>
            {data?.reportDate ? dayjs(data.reportDate).format('MMM DD, YYYY') : ''}
          </Typography>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        {control}
        <Tooltip title="Reload dashboard data">
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem' }}
            >
              Reload
            </Button>
          </span>
        </Tooltip>
      </Paper>
      </ReportPageToolbar>

      {isError && <Alert severity="error" sx={{ mb: 1 }}>Failed to load inventory.</Alert>}

      {/* FilterBar (Search + Columns) đã bỏ để tiết kiệm không gian — per-column ▽
          filter + resize vẫn ở header. */}
      <Box
        className="app-dataframe"
        sx={{
          zoom,
          border: '1px solid #dfe3e8',
          borderRadius: '8px',
          '& .app-dataframe-toolbar': { background: '#fff', borderBottom: '1px solid #dfe3e8' },
          // Body cells → match PnL/Cashflows sizing + grid colour (override global 0.72rem)
          '& .MuiTableCell-root': {
            fontSize: '0.68rem',
            lineHeight: 1.25,
            borderBottomColor: '#e8ecef',
            whiteSpace: 'nowrap',
            // Auto-width per device + min floor so columns never wrap; overflow →
            // horizontal scroll. Frozen/resized cols set inline width → override.
            minWidth: { xs: 78, sm: 88 },
          },
          // Header cells → solid forest, white, weight 600, compact (beat global cream/teal/900 !important)
          // Opaque pale gold header (#fdf9ed) + dark forest text — same as PnL,
          // opaque so scroll rows don't bleed through. .MuiTableHead-root ancestor
          // beats the global cream !important incl. frozen "Grade".
          '& .MuiTableHead-root .app-table-header-cell.MuiTableCell-root, & .MuiTableHead-root .MuiTableCell-head, & .MuiTableHead-root th.app-sticky-col, & .MuiTableHead-root th.app-sticky-col-2': {
            backgroundColor: '#fdf9ed !important',
            backgroundImage: 'none !important',
            color: '#0b3633 !important',
            fontWeight: 800,
            fontSize: '0.66rem',
            borderTop: 'none',
            borderBottom: '1px solid #f0d896',
            boxShadow: 'none',
          },
        }}
      >
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
          {(isLoading || isFetching) && <CircularProgress size={15} />}
          <Typography variant="caption">Total: {codeCount}</Typography>
          <TablePagination
            component="div"
            count={visibleFlat.length}
            page={rowsPerPage === -1 ? 0 : page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
            rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
            labelRowsPerPage="Rows"
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : rows.length === 0 ? (
            <BrandState
              title="No inventory rows"
              description="Try adjusting the search or the column filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <>
                  {/* Tầng 1 — warehouse group (Grade rowSpan 2 tại vị trí của nó). */}
                  <TableRow>
                    {headerRuns.map((run) =>
                      run.group === null
                        ? run.cols.map((col) => renderLeafHeaderCell(col, 2))
                        : (
                          <TableCell
                            key={run.cols[0].key}
                            colSpan={run.cols.length}
                            align="center"
                            className="app-table-header-cell"
                            sx={{
                              ...HEAD_FONT_SX,
                              backgroundImage: 'none',
                              backgroundColor: '#0b3633',
                              color: '#fff',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              userSelect: 'text',
                            }}
                          >
                            {run.group}
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                  {/* Tầng 2 — measure leaf: ▽ + resize. */}
                  <TableRow>
                    {visibleColumns.filter((c) => c.group).map((col) => renderLeafHeaderCell(col))}
                  </TableRow>
                </>
              )}
              fixedFooterContent={
                totalFlat
                  ? () => (
                    <TableRow>
                      {visibleColumns.map((col) => {
                        const key = col.key
                        const isFrozen = FROZEN_KEYS.has(key)
                        const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                        const { text, neg } = col.kind === 'number'
                          ? fmtNum(totalFlat[key] as number | null)
                          : { text: 'Total', neg: false }
                        return (
                          <TableCell
                            key={key}
                            align={col.align}
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={{
                              ...(w ? { width: w, minWidth: w, maxWidth: w } : {}),
                              // `.app-sticky-col` forces white bg → inline override to summary tint.
                              ...(isFrozen ? { background: '#e8f1ee' } : {}),
                            }}
                            sx={{
                              ...BODY_FONT_SX,
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              bgcolor: '#e8f1ee',
                              border: 0,
                              borderTop: '1px solid #dfe3e8',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: neg ? '#d32f2f' : undefined,
                            }}
                          >
                            {text}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                  : undefined
              }
              itemContent={(_: number, row: FlatRow) =>
                visibleColumns.map((col) => {
                  if (col.kind === 'number') return renderNumberCell(row, col)
                  const key = col.key
                  const isFrozen = FROZEN_KEYS.has(key)
                  const isCategory = row.rowType === 'category'
                  const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  // Frozen cell bg phải MATCH row state (skill §7.6): CSS
                  // `.app-sticky-col` ép trắng → row category cần inline style
                  // (specificity 1,0,0,0) tô cùng màu subtotal với cả dòng.
                  const cellStyle: CSSProperties = {}
                  if (w) { cellStyle.width = w; cellStyle.minWidth = w; cellStyle.maxWidth = w }
                  // Frozen cell bg must match row state — `.app-sticky-col` forces white.
                  if (isFrozen) cellStyle.background = isCategory ? '#e8f1ee' : '#fff'
                  return (
                    <TableCell
                      key={key}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={Object.keys(cellStyle).length ? cellStyle : undefined}
                      sx={{
                        ...BODY_FONT_SX,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'top',
                        border: 0,
                        borderBottom: '1px solid #e8ecef',
                        bgcolor: isCategory ? '#e8f1ee' : '#fff',
                        fontWeight: isCategory ? 700 : 400,
                        pl: isCategory ? 0.5 : 2.5,
                      }}
                    >
                      {isCategory ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleCollapse(row.category)}
                            sx={{ p: 0 }}
                            aria-label={collapsed.has(row.category) ? 'Expand' : 'Collapse'}
                          >
                            {collapsed.has(row.category) ? (
                              <KeyboardArrowRightIcon sx={{ fontSize: 13 }} />
                            ) : (
                              <KeyboardArrowDownIcon sx={{ fontSize: 13 }} />
                            )}
                          </IconButton>
                          {row.label}
                        </Box>
                      ) : (
                        row.label
                      )}
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%', borderCollapse: 'separate', borderSpacing: 0 }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              }}
            />
          )}
        </Box>

        {/* Global Search popover */}
        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Grade, code..."
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        {/* Per-column filter popover (1 instance duy nhất) */}
        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
